import {useCookieStorage} from '@github-ui/use-safe-storage/cookie-storage'
import {defaultPaneWidth, type PaneWidth, usePaneWidth, type UsePaneWidthResult} from '@primer/react'
import type {RefObject} from 'react'
import {useCallback} from 'react'

/**
 * Site-wide cookie key for the sidebar's resized width. Sharing one key lets a
 * user's chosen width carry across surfaces; pass a custom `storageKey` only
 * when a surface needs an independent width.
 */
export const GLOBAL_SIDEBAR_WIDTH_STORAGE_KEY = 'primer_sidebar_width'

/**
 * The preset expanded widths (`small` / `medium` / `large`) map to
 * `@primer/react`'s {@link defaultPaneWidth} so the sidebar defaults stay in
 * lockstep with `PageLayout.Pane`.
 */
export type ExpandedWidth = PaneWidth

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export interface UseResizableSidebarWidthOptions {
  /** The preset the sidebar defaults to (and resets to). */
  expandedWidth: ExpandedWidth
  /** Smallest width the sidebar can be dragged to, in pixels. */
  minWidth: number
  /** Largest width the sidebar can be dragged to, in pixels. */
  maxWidth: number
  /**
   * Cookie key used to persist the width. Defaults to
   * {@link GLOBAL_SIDEBAR_WIDTH_STORAGE_KEY}. Pass a custom key only when a
   * surface needs an independent width.
   */
  storageKey?: string
  /**
   * Server-read seed so SSR markup matches the client's first paint (prevents a
   * width flash on hard navigation). Falls back to the `expandedWidth` preset.
   */
  initialWidth?: number
  /** Called with the committed width whenever a resize operation ends. */
  onResizeEnd?: (width: number) => void
  /** The resizable element, forwarded to `usePaneWidth`. */
  paneRef: RefObject<HTMLDivElement | null>
  /** The drag handle element, forwarded to `usePaneWidth`. */
  handleRef: RefObject<HTMLDivElement | null>
}

/**
 * Cookie-backed, SSR-safe width state for a resizable {@link CollapsibleSidebar}.
 *
 * Delegates the width math (clamping, reset, drag-time ref) to `@primer/react`'s
 * {@link usePaneWidth} in controlled mode: the cookie value is passed in as
 * `currentWidth`, and `onResizeEnd` writes the committed width back to the
 * cookie. This mirrors the persistence approach of `useCollapsibleSidebarState`
 * — seed the first render with `initialWidth` (the server-read cookie value) to
 * avoid a flash on hard navigation — while reusing Primer's resize primitive
 * instead of duplicating it.
 */
export function useResizableSidebarWidth({
  expandedWidth,
  minWidth,
  maxWidth,
  storageKey = GLOBAL_SIDEBAR_WIDTH_STORAGE_KEY,
  initialWidth,
  onResizeEnd,
  paneRef,
  handleRef,
}: UseResizableSidebarWidthOptions): UsePaneWidthResult {
  const defaultWidth = defaultPaneWidth[expandedWidth]
  const fallbackWidth = initialWidth ?? defaultWidth
  const [storedWidth, setStoredWidth] = useCookieStorage<unknown>(storageKey, fallbackWidth)
  const controlledWidth = clamp(
    typeof storedWidth === 'number' && Number.isFinite(storedWidth) ? storedWidth : fallbackWidth,
    minWidth,
    maxWidth,
  )

  const handleResizeEnd = useCallback(
    (width: number) => {
      const rounded = Math.round(width)
      setStoredWidth(rounded)
      onResizeEnd?.(rounded)
    },
    [setStoredWidth, onResizeEnd],
  )

  return usePaneWidth({
    width: {min: `${minWidth}px`, default: `${defaultWidth}px`, max: `${maxWidth}px`},
    minWidth,
    resizable: true,
    constrainToViewport: true,
    paneRef,
    handleRef,
    currentWidth: controlledWidth,
    onResizeEnd: handleResizeEnd,
    formatValueText: width => `${width} pixels`,
  })
}

export function getSeededSidebarWidth({
  expandedWidth,
  minWidth,
  maxWidth,
  initialWidth,
}: {
  expandedWidth: ExpandedWidth
  minWidth: number
  maxWidth: number
  initialWidth?: number
}): number {
  return clamp(initialWidth ?? defaultPaneWidth[expandedWidth], minWidth, maxWidth)
}
